import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import MainLayout from '../layout/components/MainLayout';
import ProtectedRoute from './ProtectedRoute';
import AdminRoute from '../admin/components/AdminRoute';
import Loader from '../components/Loader';

// Helper for lazy loading with automatic retry on stale deployment chunk errors
const lazyWithRetry = (componentImport) =>
  lazy(async () => {
    const pageHasAlreadyBeenRefreshed = JSON.parse(
      window.sessionStorage.getItem('page_has_been_refreshed') || 'false'
    );

    try {
      const component = await componentImport();
      window.sessionStorage.setItem('page_has_been_refreshed', 'false');
      return component;
    } catch (error) {
      console.warn('Chunk load error caught, reloading for latest bundle...', error);
      if (!pageHasAlreadyBeenRefreshed) {
        window.sessionStorage.setItem('page_has_been_refreshed', 'true');
        window.location.reload();
        return new Promise(() => {}); // Suspend rendering until reload completes
      }
      throw error;
    }
  });

// Lazy load pages with retry mechanism
const LandingPage = lazyWithRetry(() => import('../landing/pages/LandingPage'));
const Dashboard = lazyWithRetry(() => import('../dashboard/pages/Dashboard'));
const Products = lazyWithRetry(() => import('../products/pages/Products'));
const AddProductPage = lazyWithRetry(() => import('../products/pages/AddProductPage'));
const ImportProductsPage = lazyWithRetry(() => import('../products/pages/ImportProductsPage'));
const Orders = lazyWithRetry(() => import('../orders/pages/Orders'));
const Earnings = lazyWithRetry(() => import('../earnings/pages/Earnings'));
const Videos = lazyWithRetry(() => import('../videos/pages/Videos'));
const InstagramAutomation = lazyWithRetry(() => import('../instagram/pages/InstagramAutomation'));
const InstagramCallback = lazyWithRetry(() => import('../instagram/pages/InstagramCallback'));
const Profile = lazyWithRetry(() => import('../profile/pages/Profile'));
const LoginPage = lazyWithRetry(() => import('../auth/pages/LoginPage'));
const SignupPage = lazyWithRetry(() => import('../auth/pages/SignupPage'));
const ResetPasswordPage = lazyWithRetry(() => import('../auth/pages/ResetPasswordPage'));
const AdminPage = lazyWithRetry(() => import('../admin/pages/AdminPage'));

// Live Drops Pages
const LiveDropsPage = lazyWithRetry(() => import('../livedrops/pages/LiveDropsPage'));
const CreateLiveDropPage = lazyWithRetry(() => import('../livedrops/pages/CreateLiveDropPage'));
const LiveHostRoomPage = lazyWithRetry(() => import('../livedrops/pages/LiveHostRoomPage'));
const PublicDropBoardPage = lazyWithRetry(() => import('../livedrops/pages/PublicDropBoardPage'));


const Router = () => {
  return (
    <Suspense fallback={<Loader fullScreen={true} />}>
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<LandingPage />} />
        
        {/* Public Live Drop Buyer Routes */}
        <Route path="/drop/:id" element={<PublicDropBoardPage />} />
        <Route path="/live/:id" element={<PublicDropBoardPage />} />
        
        {/* Auth Routes */}
        <Route path="/auth/login" element={<LoginPage />} />
        <Route path="/auth/signup" element={<SignupPage />} />
        <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
        <Route path="/instagram-callback" element={<InstagramCallback />} />
        
        {/* Protected Routes */}
        <Route element={
          <ProtectedRoute>
            <MainLayout />
          </ProtectedRoute>
        }>
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="live-drops" element={<LiveDropsPage />} />
          <Route path="live-drops/create" element={<CreateLiveDropPage />} />
          <Route path="live-drops/edit/:id" element={<CreateLiveDropPage />} />
          <Route path="live-drops/:id/host" element={<LiveHostRoomPage />} />
          
          <Route path="products" element={<Products />} />
          <Route path="products/add" element={<AddProductPage />} />
          <Route path="products/import" element={<ImportProductsPage />} />
          <Route path="products/edit/:id" element={<AddProductPage />} />

          <Route path="orders" element={<Orders />} />
          <Route path="earnings" element={<Earnings />} />
          <Route path="videos" element={<Videos />} />
          <Route path="instagram" element={<InstagramAutomation />} />
          <Route path="profile" element={<Profile />} />
          <Route path="admin" element={
            <AdminRoute>
              <AdminPage />
            </AdminRoute>
          } />
        </Route>

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
};

export default Router;
